import type {
  RecurringServicesContractBurndownDto as ShiOneBurndownDto,
  RecurringServicesContractMonthlyBreakdownDto as ShiOneMonthlyBreakdownDto,
  RecurringServicesContractDetailRecordDto as ShiOneDetailRecordDto,
  RecurringServicesContractDetailDto as ShiOneDetailDto,
  RoleUsageDto as ShiOneRoleUsageDto,
} from "@lib/ShiOneClient";
import type {
  RecurringServicesContractDetailDto,
  RecurringServicesContractBurndownDto,
  RecurringServicesContractMonthlyBreakdownDto,
  RecurringServicesContractDetailRecordDto,
  RoleUsageDto,
  RecurringServicesRoleType,
  ExpertSupportDetailType,
  ExpertSupportDetailStatus,
} from "shared-ui/src/features/contracts-feature/canonical";

function mapRoleUsage(dto: ShiOneRoleUsageDto | undefined | null): RoleUsageDto | undefined {
  if (!dto) return undefined;
  return {
    roleType: dto.roleType as RecurringServicesRoleType,
    hours: dto.hours,
    dollars: dto.dollars,
    percentageOfTotal: dto.percentageOfTotal,
  };
}

function mapBurndown(dto: ShiOneBurndownDto | undefined | null): RecurringServicesContractBurndownDto | undefined {
  if (!dto) return undefined;
  return {
    contractId: dto.contractId,
    contractName: dto.contractName,
    accountId: dto.accountId,
    isActive: dto.isActive,
    contractStartDate: dto.contractStartDate,
    contractEndDate: dto.contractEndDate,
    totalPurchased: dto.totalPurchased,
    totalPurchasedBilled: dto.totalPurchasedBilled,
    totalPurchasedUnbilled: dto.totalPurchasedUnbilled,
    totalUsed: dto.totalUsed,
    currentBalance: dto.currentBalance,
    totalDollarsUsageBilled: dto.totalDollarsUsageBilled,
    totalDollarsUsageUnbilled: dto.totalDollarsUsageUnbilled,
    utilizationPercentage: dto.utilizationPercentage,
    estimatedMonthlyBurnRate: dto.estimatedMonthlyBurnRate,
    estimatedDaysRemaining: dto.estimatedDaysRemaining,
    totalChargeCount: dto.totalChargeCount,
    totalTimeEntryCount: dto.totalTimeEntryCount,
    proactiveDollarsUsed: dto.proactiveDollarsUsed,
    nonProactiveDollarsUsed: dto.nonProactiveDollarsUsed,
    usageByRole: dto.usageByRole
      ?.map(mapRoleUsage)
      .filter((r): r is RoleUsageDto => r !== undefined),
    expertSupportTier: dto.expertSupportTier,
  };
}

function mapMonthlyBreakdown(dto: ShiOneMonthlyBreakdownDto | undefined | null): RecurringServicesContractMonthlyBreakdownDto | undefined {
  if (!dto) return undefined;
  return {
    contractId: dto.contractId,
    accountId: dto.accountId,
    year: dto.year,
    month: dto.month,
    openingBalance: dto.openingBalance,
    closingBalance: dto.closingBalance,
    chargesAdded: dto.chargesAdded,
    consumed: dto.consumed,
    usageByRole: dto.usageByRole
      ?.map(mapRoleUsage)
      .filter((r): r is RoleUsageDto => r !== undefined),
  };
}

function mapDetailRecord(dto: ShiOneDetailRecordDto | undefined | null): RecurringServicesContractDetailRecordDto | undefined {
  if (!dto) return undefined;
  return {
    accountId: dto.accountId,
    contractId: dto.contractId,
    dataLoadedDateTime: dto.dataLoadedDateTime,
    detailType: dto.detailType as ExpertSupportDetailType,
    hoursWorked: dto.hoursWorked,
    month: dto.month,
    purchasedAmount: dto.purchasedAmount,
    recordCount: dto.recordCount,
    status: dto.status as ExpertSupportDetailStatus,
    ticketNumbers: dto.ticketNumbers,
    usageAmount: dto.usageAmount,
    year: dto.year,
  };
}

export function mapRecurringServicesContractDetail(
  dto: ShiOneDetailDto | undefined | null
): RecurringServicesContractDetailDto | null {
  if (!dto) return null;
  return {
    burndown: mapBurndown(dto.burndown),
    detailRecords: dto.detailRecords
      ?.map(mapDetailRecord)
      .filter((r): r is RecurringServicesContractDetailRecordDto => r !== undefined),
    monthlyBreakdowns: dto.monthlyBreakdowns
      ?.map(mapMonthlyBreakdown)
      .filter((r): r is RecurringServicesContractMonthlyBreakdownDto => r !== undefined),
  };
}

